import clsx from "clsx";
import { FC, useCallback, useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { CheckboxMui } from "@ryde/controls";
import { IRichLogData, RichLogLevel } from "@ryde/shared";

import { IRichLogsFilter, RichLogFormFields } from "./rich-logs.models";
import {
  filterRecordsExcludingLevels,
  getNormalizedRichLogLevel,
} from "./rich-logs.utils";

interface IRichLogLevelCheckboxes {
  logData: IRichLogData | undefined;
  showLoader?: boolean;
}

const LEVEL_COLOR_MAP: Record<RichLogLevel, string> = {
  [RichLogLevel.Critical]: "color_critical",
  [RichLogLevel.Error]: "color_error",
  [RichLogLevel.Warn]: "color_warn",
  [RichLogLevel.Info]: "color_info",
  [RichLogLevel.Details]: "color_details",
  [RichLogLevel.Debug]: "color_debug",
};

const ALWAYS_VISIBLE_LEVELS: readonly RichLogLevel[] = [
  RichLogLevel.Info,
  RichLogLevel.Details,
  RichLogLevel.Debug,
];

const CONDITIONAL_LEVELS: readonly RichLogLevel[] = [
  RichLogLevel.Critical,
  RichLogLevel.Error,
  RichLogLevel.Warn,
];

export const RichLogLevelCheckboxes: FC<IRichLogLevelCheckboxes> = ({
  logData,
  showLoader,
}) => {
  const { control } = useFormContext<IRichLogsFilter>();
  const { t } = useTranslation();
  const group = useWatch({ control, name: RichLogFormFields.group });
  const commaMode = useWatch({ control, name: RichLogFormFields.commaMode });
  const step = useWatch({ control, name: RichLogFormFields.step });
  const searchText = useWatch({ control, name: RichLogFormFields.searchText });
  const showDebugInfo = useWatch({
    control,
    name: RichLogFormFields.showDebugInfo,
  });
  const withAiHintsOnly = useWatch({
    control,
    name: RichLogFormFields.withAiHintsOnly,
  });
  const { records } = logData ?? {};

  const recordsForLevelCounts = useMemo(
    () =>
      filterRecordsExcludingLevels({
        records,
        group,
        step,
        searchText,
        commaMode,
        showDebugInfo,
        withAiHintsOnly,
        t,
      }),
    [
      records,
      group,
      commaMode,
      step,
      searchText,
      showDebugInfo,
      withAiHintsOnly,
      t,
    ],
  );

  const levelCounts = useMemo(() => {
    const counts = new Map<RichLogLevel, number>();

    recordsForLevelCounts.forEach(({ level }) => {
      const normalizedLevel = getNormalizedRichLogLevel(level);
      const levelForCount = normalizedLevel ?? RichLogLevel.Debug;

      counts.set(levelForCount, (counts.get(levelForCount) ?? 0) + 1);
    });

    return counts;
  }, [recordsForLevelCounts]);

  const getLogCountByLevel = useCallback(
    (level: RichLogLevel): number => {
      return levelCounts.get(level) ?? 0;
    },
    [levelCounts],
  );

  const visibleLevels = useMemo(() => {
    const conditionalLevels = CONDITIONAL_LEVELS.filter(
      level => getLogCountByLevel(level) > 0,
    );

    return [...ALWAYS_VISIBLE_LEVELS, ...conditionalLevels];
  }, [getLogCountByLevel]);

  return (
    <div className="log__filters-level-checkboxes">
      <div className="log__filters-level-row">
        <span className="log__filters-level-label">
          {t("rideProduction.formLabel.levels")}:
        </span>
        <div className="log__filters-level-checkboxes-container">
          <Controller
            name={RichLogFormFields.levels}
            control={control}
            render={({ field: { value, onChange } }) => {
              const selectedLevels: RichLogLevel[] = value ?? [];

              const handleLevelToggle = (level: RichLogLevel): void => {
                const newLevels = selectedLevels.includes(level)
                  ? selectedLevels.filter(l => l !== level)
                  : [...selectedLevels, level];
                onChange(newLevels);
              };

              return (
                <>
                  {visibleLevels.map(level => {
                    const levelCount = getLogCountByLevel(level);

                    return (
                      <button
                        key={level}
                        type="button"
                        className="log__filters-level-checkbox-item"
                        onClick={() => handleLevelToggle(level)}
                        aria-label={`${level} (${levelCount})`}
                      >
                        {/* eslint-disable-next-line jsx-a11y-x/click-events-have-key-events, jsx-a11y-x/no-static-element-interactions -- keyboard activation is handled by the wrapping <button>; this span only stops mouse propagation */}
                        <span
                          className="log__filters-level-checkbox-control"
                          onClick={event => {
                            event.stopPropagation();
                            handleLevelToggle(level);
                          }}
                        >
                          <CheckboxMui
                            checked={selectedLevels.includes(level)}
                          />
                        </span>
                        <span
                          className={clsx(
                            "log__filters-level-label",
                            LEVEL_COLOR_MAP[level],
                          )}
                        >
                          {level} ({levelCount})
                        </span>
                      </button>
                    );
                  })}
                </>
              );
            }}
          />
        </div>
      </div>
      <div
        className={clsx("log__filters-level-loader", { loading: showLoader })}
        aria-hidden
      />
    </div>
  );
};
